import { useState } from 'react';
import { Loader2, Plus, Trash2, Check, X, ArrowLeft } from 'lucide-react';
import {
  useRules,
  useCreateRule,
  useUpdateRule,
  useDeleteRule,
  type Rule,
  type RuleCondition,
  type RuleAction,
} from '../../hooks/useRules';
import { useTags } from '../../hooks/useTags';
import { useFolders } from '../../hooks/useFolders';
import type { RuleInput } from '@webmail/shared';

// ---- Etichette leggibili per i dropdown (allineate agli enum dei tipi shared) ----
const CONDITION_LABELS: Record<RuleCondition['type'], string> = {
  from_contains: 'Mittente contiene',
  from_equals: 'Mittente è',
  to_contains: 'Destinatario contiene',
  subject_contains: 'Oggetto contiene',
  subject_regex: 'Oggetto (regex)',
  body_contains: 'Corpo contiene',
  has_attachment: 'Ha allegato',
  size_gt: 'Dimensione (byte) >',
  ai_prompt: 'Prompt AI',
};

const ACTION_LABELS: Record<RuleAction['type'], string> = {
  add_tag: 'Aggiungi tag',
  remove_tag: 'Rimuovi tag',
  move_to_folder: 'Sposta in cartella',
  set_priority: 'Imposta priorità',
  mark_read: 'Segna come letto',
  flag: 'Metti stella',
  summarize: 'Riassumi (AI)',
  extract_entities: 'Estrai entità (AI)',
  parse_attachments_to_md: 'Allegati → Markdown',
  trigger_webhook: 'Attiva webhook',
  forward: 'Inoltra a',
  auto_reply: 'Risposta automatica',
};

const PRIORITIES = ['urgent', 'high', 'normal', 'low'] as const;

interface RuleDraft {
  id?: string;
  name: string;
  enabled: boolean;
  priority: number;
  conditions: RuleCondition[];
  actions: RuleAction[];
}

function defaultCondition(type: RuleCondition['type']): RuleCondition {
  switch (type) {
    case 'has_attachment':
      return { type, value: true };
    case 'size_gt':
      return { type, value: 0 };
    case 'ai_prompt':
      return { type, prompt: '' };
    default:
      return { type, value: '' };
  }
}

function defaultAction(type: RuleAction['type']): RuleAction {
  switch (type) {
    case 'add_tag':
    case 'remove_tag':
      return { type, tag_id: '' };
    case 'move_to_folder':
      return { type, folder_id: '' };
    case 'set_priority':
      return { type, value: 'normal' };
    case 'trigger_webhook':
      return { type, webhook_id: '' };
    case 'forward':
      return { type, to: '' };
    case 'auto_reply':
      return { type, template: '' };
    default:
      return { type } as RuleAction;
  }
}

function emptyDraft(): RuleDraft {
  return {
    name: '',
    enabled: true,
    priority: 100,
    conditions: [defaultCondition('from_contains')],
    actions: [defaultAction('add_tag')],
  };
}

// TAB Regole: lista + editor con condizioni (AND) e azioni dinamiche
export function RulesTab() {
  const { data: rules, isLoading } = useRules();
  const updateRule = useUpdateRule();
  const deleteRule = useDeleteRule();
  const [draft, setDraft] = useState<RuleDraft | null>(null);

  if (draft) {
    return <RuleEditor draft={draft} onClose={() => setDraft(null)} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-zinc-100">Regole</h3>
          <p className="text-[11px] text-zinc-500">
            Automazioni eseguite sui messaggi in arrivo (condizioni in AND).
          </p>
        </div>
        <button
          onClick={() => setDraft(emptyDraft())}
          className="flex items-center gap-1.5 rounded-md bg-accent-500 px-2.5 py-1.5 text-xs font-medium text-white transition hover:bg-accent-600"
        >
          <Plus size={13} /> Nuova regola
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 size={16} className="animate-spin text-zinc-600" />
        </div>
      ) : !rules || rules.length === 0 ? (
        <p className="py-8 text-center text-xs text-zinc-500">Nessuna regola definita.</p>
      ) : (
        <ul className="divide-y divide-zinc-800/70 rounded-md border border-zinc-800">
          {rules.map((r) => (
            <li key={r.id} className="flex items-center justify-between px-3 py-2.5">
              <button
                onClick={() => setDraft(ruleToDraft(r))}
                className="flex-1 text-left"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-zinc-200">{r.name}</span>
                  <span className="rounded bg-zinc-800 px-1.5 text-[10px] text-zinc-400">
                    pri {r.priority}
                  </span>
                </div>
                <div className="mt-0.5 text-[11px] text-zinc-500">
                  {r.conditions?.length ?? 0} condizioni · {r.actions?.length ?? 0} azioni
                </div>
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    updateRule.mutate({ id: r.id, body: { enabled: !r.enabled } })
                  }
                  title={r.enabled ? 'Disattiva' : 'Attiva'}
                  className={`relative h-4 w-7 rounded-full transition ${
                    r.enabled ? 'bg-accent-500' : 'bg-zinc-700'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${
                      r.enabled ? 'left-3.5' : 'left-0.5'
                    }`}
                  />
                </button>
                <button
                  onClick={() => deleteRule.mutate(r.id)}
                  className="rounded p-1 text-zinc-500 transition hover:bg-rose-500/10 hover:text-rose-400"
                  title="Elimina regola"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ruleToDraft(r: Rule): RuleDraft {
  return {
    id: r.id,
    name: r.name,
    enabled: r.enabled,
    priority: r.priority,
    conditions: (r.conditions ?? []) as RuleCondition[],
    actions: (r.actions ?? []) as RuleAction[],
  };
}

function RuleEditor({ draft, onClose }: { draft: RuleDraft; onClose: () => void }) {
  const [state, setState] = useState<RuleDraft>(draft);
  const createRule = useCreateRule();
  const updateRule = useUpdateRule();
  const { data: tags } = useTags();
  const { data: folders } = useFolders();

  const busy = createRule.isPending || updateRule.isPending;

  function patchCondition(i: number, patch: Record<string, unknown>) {
    setState((s) => ({
      ...s,
      conditions: s.conditions.map((c, idx) =>
        idx === i ? ({ ...c, ...patch } as RuleCondition) : c,
      ),
    }));
  }

  function patchAction(i: number, patch: Record<string, unknown>) {
    setState((s) => ({
      ...s,
      actions: s.actions.map((a, idx) =>
        idx === i ? ({ ...a, ...patch } as RuleAction) : a,
      ),
    }));
  }

  async function save() {
    const payload: RuleInput = {
      name: state.name.trim(),
      enabled: state.enabled,
      priority: Number(state.priority) || 0,
      conditions: state.conditions,
      actions: state.actions,
    };
    if (!payload.name || payload.conditions.length === 0 || payload.actions.length === 0) return;
    if (state.id) {
      await updateRule.mutateAsync({ id: state.id, body: payload });
    } else {
      await createRule.mutateAsync(payload);
    }
    onClose();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 text-xs text-zinc-400 transition hover:text-zinc-200"
        >
          <ArrowLeft size={13} /> Regole
        </button>
        <button
          onClick={save}
          disabled={busy || !state.name.trim()}
          className="flex items-center gap-1.5 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-accent-600 disabled:opacity-40"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          {state.id ? 'Salva modifiche' : 'Crea regola'}
        </button>
      </div>

      {/* Meta regola */}
      <div className="grid grid-cols-[1fr_auto_auto] items-end gap-3">
        <div>
          <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-zinc-500">
            Nome
          </label>
          <input
            value={state.name}
            onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
            placeholder="Es. Fatture in cartella"
            className="w-full rounded-md border border-zinc-800 bg-zinc-950/60 px-2.5 py-1.5 text-sm outline-none focus:border-accent-500/50"
          />
        </div>
        <div className="w-24">
          <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-zinc-500">
            Priorità
          </label>
          <input
            type="number"
            value={state.priority}
            onChange={(e) => setState((s) => ({ ...s, priority: Number(e.target.value) }))}
            className="w-full rounded-md border border-zinc-800 bg-zinc-950/60 px-2.5 py-1.5 text-sm outline-none focus:border-accent-500/50"
          />
        </div>
        <label className="flex items-center gap-1.5 pb-2 text-xs text-zinc-300">
          <input
            type="checkbox"
            checked={state.enabled}
            onChange={(e) => setState((s) => ({ ...s, enabled: e.target.checked }))}
            className="accent-accent-500"
          />
          Attiva
        </label>
      </div>

      {/* Condizioni */}
      <Section
        title="Condizioni (tutte in AND)"
        onAdd={() =>
          setState((s) => ({ ...s, conditions: [...s.conditions, defaultCondition('from_contains')] }))
        }
      >
        {state.conditions.map((c, i) => (
          <Row
            key={i}
            onRemove={
              state.conditions.length > 1
                ? () =>
                    setState((s) => ({
                      ...s,
                      conditions: s.conditions.filter((_, idx) => idx !== i),
                    }))
                : undefined
            }
          >
            <select
              value={c.type}
              onChange={(e) =>
                setState((s) => ({
                  ...s,
                  conditions: s.conditions.map((cc, idx) =>
                    idx === i ? defaultCondition(e.target.value as RuleCondition['type']) : cc,
                  ),
                }))
              }
              className={selectCls}
            >
              {Object.entries(CONDITION_LABELS).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
            <ConditionValue cond={c} onPatch={(p) => patchCondition(i, p)} />
          </Row>
        ))}
      </Section>

      {/* Azioni */}
      <Section
        title="Azioni"
        onAdd={() => setState((s) => ({ ...s, actions: [...s.actions, defaultAction('add_tag')] }))}
      >
        {state.actions.map((a, i) => (
          <Row
            key={i}
            onRemove={
              state.actions.length > 1
                ? () =>
                    setState((s) => ({
                      ...s,
                      actions: s.actions.filter((_, idx) => idx !== i),
                    }))
                : undefined
            }
          >
            <select
              value={a.type}
              onChange={(e) =>
                setState((s) => ({
                  ...s,
                  actions: s.actions.map((aa, idx) =>
                    idx === i ? defaultAction(e.target.value as RuleAction['type']) : aa,
                  ),
                }))
              }
              className={selectCls}
            >
              {Object.entries(ACTION_LABELS).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
            <ActionValue
              action={a}
              onPatch={(p) => patchAction(i, p)}
              tags={tags ?? []}
              folders={folders ?? []}
            />
          </Row>
        ))}
      </Section>
    </div>
  );
}

const selectCls =
  'rounded-md border border-zinc-800 bg-zinc-950/60 px-2 py-1.5 text-xs text-zinc-200 outline-none focus:border-accent-500/50';
const inputCls =
  'flex-1 rounded-md border border-zinc-800 bg-zinc-950/60 px-2.5 py-1.5 text-xs outline-none focus:border-accent-500/50';

function ConditionValue({
  cond,
  onPatch,
}: {
  cond: RuleCondition;
  onPatch: (patch: Record<string, unknown>) => void;
}) {
  const anyCond = cond as Record<string, unknown>;
  if (cond.type === 'has_attachment') {
    return (
      <select
        value={anyCond.value === false ? 'false' : 'true'}
        onChange={(e) => onPatch({ value: e.target.value === 'true' })}
        className={selectCls}
      >
        <option value="true">Sì</option>
        <option value="false">No</option>
      </select>
    );
  }
  if (cond.type === 'size_gt') {
    return (
      <input
        type="number"
        value={(anyCond.value as number) ?? 0}
        onChange={(e) => onPatch({ value: Number(e.target.value) })}
        placeholder="byte"
        className={inputCls}
      />
    );
  }
  if (cond.type === 'ai_prompt') {
    return (
      <textarea
        value={(anyCond.prompt as string) ?? ''}
        onChange={(e) => onPatch({ prompt: e.target.value })}
        placeholder="Descrivi cosa deve valutare l'AI…"
        rows={2}
        className={`${inputCls} resize-y`}
      />
    );
  }
  return (
    <input
      value={(anyCond.value as string) ?? ''}
      onChange={(e) => onPatch({ value: e.target.value })}
      placeholder="valore…"
      className={inputCls}
    />
  );
}

function ActionValue({
  action,
  onPatch,
  tags,
  folders,
}: {
  action: RuleAction;
  onPatch: (patch: Record<string, unknown>) => void;
  tags: { id: string; name: string; color: string }[];
  folders: { id: string; name: string; path: string }[];
}) {
  const anyAction = action as Record<string, unknown>;
  if (action.type === 'add_tag' || action.type === 'remove_tag') {
    return (
      <select
        value={(anyAction.tag_id as string) ?? ''}
        onChange={(e) => onPatch({ tag_id: e.target.value })}
        className={selectCls}
      >
        <option value="">— seleziona tag —</option>
        {tags.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
    );
  }
  if (action.type === 'move_to_folder') {
    return (
      <select
        value={(anyAction.folder_id as string) ?? ''}
        onChange={(e) => onPatch({ folder_id: e.target.value })}
        className={selectCls}
      >
        <option value="">— seleziona cartella —</option>
        {folders.map((f) => (
          <option key={f.id} value={f.id}>
            {f.path ?? f.name}
          </option>
        ))}
      </select>
    );
  }
  if (action.type === 'set_priority') {
    return (
      <select
        value={(anyAction.value as string) ?? 'normal'}
        onChange={(e) => onPatch({ value: e.target.value })}
        className={selectCls}
      >
        {PRIORITIES.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
      </select>
    );
  }
  if (action.type === 'trigger_webhook') {
    return (
      <input
        value={(anyAction.webhook_id as string) ?? ''}
        onChange={(e) => onPatch({ webhook_id: e.target.value })}
        placeholder="ID webhook"
        className={inputCls}
      />
    );
  }
  if (action.type === 'forward') {
    return (
      <input
        type="email"
        value={(anyAction.to as string) ?? ''}
        onChange={(e) => onPatch({ to: e.target.value })}
        placeholder="destinatario@dominio.com"
        className={inputCls}
      />
    );
  }
  if (action.type === 'auto_reply') {
    return (
      <textarea
        value={(anyAction.template as string) ?? ''}
        onChange={(e) => onPatch({ template: e.target.value })}
        placeholder="Testo della risposta automatica…"
        rows={2}
        className={`${inputCls} resize-y`}
      />
    );
  }
  // Azioni senza parametri: mark_read, flag, summarize, extract_entities, parse_attachments_to_md
  return <span className="flex-1 text-[11px] italic text-zinc-500">nessun parametro</span>;
}

function Section({
  title,
  onAdd,
  children,
}: {
  title: string;
  onAdd: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950/30 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
          {title}
        </span>
        <button
          onClick={onAdd}
          className="flex items-center gap-1 rounded border border-zinc-700 px-1.5 py-0.5 text-[11px] text-zinc-300 transition hover:bg-zinc-800"
        >
          <Plus size={11} /> Aggiungi
        </button>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({
  children,
  onRemove,
}: {
  children: React.ReactNode;
  onRemove?: () => void;
}) {
  return (
    <div className="flex items-start gap-2">
      {children}
      <button
        onClick={onRemove}
        disabled={!onRemove}
        className="mt-0.5 rounded p-1.5 text-zinc-500 transition hover:bg-rose-500/10 hover:text-rose-400 disabled:opacity-20"
        title="Rimuovi"
      >
        <X size={13} />
      </button>
    </div>
  );
}
